import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { GiftForm } from "./GiftForm";

const STATUSES = [
  { id: "pending_payment", label: "Awaiting payment" },
  { id: "paid", label: "Paid / active" },
  { id: "redeemed", label: "Redeemed" },
  { id: "cancelled", label: "Cancelled" },
];

export default function GiftsTab() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [gifts, setGifts] = useState([]);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => api.get("/admin/gift-certificates").then((res) => setGifts(res.data)), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (g, status) => {
    try {
      await api.patch(`/admin/gift-certificates/${g.id}`, { status });
      toast.success(`Marked ${g.code} as ${STATUSES.find((s) => s.id === status)?.label.toLowerCase()}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const pending = gifts.filter((g) => g.status === "pending_payment").length;

  return (
    <div data-testid="gifts-tab">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-serif text-3xl font-medium text-forest">Gift Certificates</h2>
          <p className="mt-1 text-sm text-moss">{pending} awaiting payment. Mark a certificate paid once the buyer pays at the salon or by phone.</p>
        </div>
        {isOwner && <button data-testid="gift-new-button" onClick={() => setAdding(true)} className="rounded-md bg-forest px-4 py-2 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark">+ New Gift Certificate</button>}
      </div>
      {adding && <div className="mt-6"><GiftForm onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} /></div>}

      {gifts.length === 0 ? (
        <p className="mt-8 text-sm text-moss">No gift certificates yet.</p>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-lg border border-linen bg-ivory">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-linen text-xs uppercase tracking-[0.1em] text-moss">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Gift</th>
                <th className="px-4 py-3">From → To</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-linen">
              {gifts.map((g) => (
                <tr key={g.id} data-testid={`gift-row-${g.id}`}>
                  <td className="px-4 py-3 font-semibold text-forest">{g.code}</td>
                  <td className="px-4 py-3 text-forest">{g.value_label}{g.message && <p className="mt-0.5 max-w-xs truncate text-xs italic text-moss">“{g.message}”</p>}</td>
                  <td className="px-4 py-3 text-moss">
                    <p className="text-forest">{g.purchaser_name}</p>
                    <p className="text-xs">{g.purchaser_email}</p>
                    <p className="mt-1 text-xs">→ {g.recipient_name}{g.recipient_email ? ` (${g.recipient_email})` : ""}</p>
                  </td>
                  <td className="px-4 py-3 text-moss">{g.created_at.slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <select data-testid={`gift-status-${g.id}`} value={g.status} onChange={(e) => setStatus(g, e.target.value)} className="rounded-md border border-linen bg-ivory px-2 py-1.5 text-xs outline-none focus:border-forest">
                      {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right"><a href={`/gift/${g.id}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-forest hover:underline">View</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
